import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import {
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Pagination,
    Typography,
} from '@mui/material'

import Get from '../../components/Get'
import Header from '../../components/Header'
import useDebounce from '../../hooks/useDebounce'
import useRouteGuard from '../../hooks/useRouteGuard'
import { ANPRHitsListResponse } from '../vehicles/types'
import { formatDateTime } from '../../utils/datetime'

const RESULTS_PER_PAGE = 10

const S = {
    Container: styled.div`
        padding: 24px;
    `,
    Search: styled(Form.Control)`
        margin: 24px 0;
        max-width: 100%;
        width: 200px;

        @media (max-width: 600px) {
            width: 100%;
        }
    `,
}

type OrderField = 'latest_sighting_date' | 'plate' | 'hit_count'
type OrderDir = 'asc' | 'desc'

const ANPRHitsView: React.FC = () => {
    useRouteGuard('canReadVehicles')
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [orderBy, setOrderBy] = useState<OrderField>('latest_sighting_date')
    const [orderDir, setOrderDir] = useState<OrderDir>('desc')
    const debouncedSearch = useDebounce(search)

    const handleSort = (field: OrderField) => {
        if (orderBy === field) {
            setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')
        } else {
            setOrderBy(field)
            setOrderDir('desc')
        }
        setPage(1)
    }

    const ordering = orderDir === 'desc' ? `-${orderBy}` : orderBy
    const params: Record<string, string> = {
        limit: `${RESULTS_PER_PAGE}`,
        offset: `${(page - 1) * RESULTS_PER_PAGE}`,
        ordering,
    }
    if (debouncedSearch) params.search = debouncedSearch
    const searchParams = new URLSearchParams(params)
    const url = `vehicles/anpr-hits/?${searchParams.toString()}`

    return (
        <S.Container>
            <Stack flexDirection={{ md: 'row' }} justifyContent={{ md: 'space-between' }}>
                <Header>ANPR Hits</Header>
                <S.Search
                    placeholder="Search plate, make, model"
                    onChange={(e) => {
                        setSearch(e.target.value)
                        setPage(1)
                    }}
                />
            </Stack>

            <Get url={url} interval={30}>
                {({ data }: { data: ANPRHitsListResponse }) => (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total results: {data.count}
                            </Typography>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'plate'}
                                                direction={orderBy === 'plate' ? orderDir : 'asc'}
                                                onClick={() => handleSort('plate')}
                                            >
                                                Plate
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Make</TableCell>
                                        <TableCell>Model</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'hit_count'}
                                                direction={orderBy === 'hit_count' ? orderDir : 'asc'}
                                                onClick={() => handleSort('hit_count')}
                                            >
                                                Hits
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Latest Camera</TableCell>
                                        <TableCell>Country</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'latest_sighting_date'}
                                                direction={orderBy === 'latest_sighting_date' ? orderDir : 'asc'}
                                                onClick={() => handleSort('latest_sighting_date')}
                                            >
                                                Latest Sighting
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.results.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Link to={`/vehicles/${item.id}/`}>
                                                    {item.plate}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{item.make}</TableCell>
                                            <TableCell>{item.model}</TableCell>
                                            <TableCell>{item.status}</TableCell>
                                            <TableCell>{item.hitCount}</TableCell>
                                            <TableCell>{item.latestCameraLocation}</TableCell>
                                            <TableCell>{item.latestCountry}</TableCell>
                                            <TableCell>
                                                {formatDateTime(item.latestSightingDate)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {data.results.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No ANPR hits found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination
                            sx={{ mt: 2 }}
                            count={Math.ceil(data.count / RESULTS_PER_PAGE)}
                            shape="rounded"
                            onChange={(_, p) => setPage(p)}
                            page={page}
                            color="primary"
                            siblingCount={0}
                        />
                    </>
                )}
            </Get>
        </S.Container>
    )
}

export default ANPRHitsView
